import { Button, EnrollmentHelpText } from '@/shared/components'
import { StepHeader } from '../StepHeader'
import { LicenseDateFields } from './licenseDetails/LicenseDateFields'
import { LicenseIdentityFields } from './licenseDetails/LicenseIdentityFields'
import { LicenseUploadField } from './licenseDetails/LicenseUploadField'
import { useLicenseDetailsStep } from './licenseDetails/useLicenseDetailsStep'
import type { EligibilityFormData } from './types'
import type { EnrollmentFieldEditability } from '../../utils/enrollmentHydration'

type LicenseDetailsStepProps = {
  currentStep: number
  totalSteps: number
  fieldEditability: EnrollmentFieldEditability
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onBack: () => void
  onContinue: () => void
  isSubmitting?: boolean
  errorMessage?: string | null
}

export function LicenseDetailsStep({
  currentStep,
  totalSteps,
  fieldEditability,
  formData,
  updateFormData,
  onBack,
  onContinue,
  isSubmitting = false,
  errorMessage = null
}: LicenseDetailsStepProps) {
  const isDriverLicenseStateDisabled = fieldEditability.driversLicenseState === false
  const isDriverLicenseNumberDisabled = fieldEditability.driversLicenseNumber === false
  const isDriverLicenseIssueDateDisabled = fieldEditability.driversLicenseIssueDate === false
  const isDriverLicenseExpiryDisabled = fieldEditability.driversLicenseExpiry === false

  const {
    handleContinueClick,
    handleDriverLicenseFileChange,
    handleDriverLicenseNumberChange,
    handleDriverLicenseStateChange,
    handleDriverLicenseUploadClick,
    handleExpirationDateChange,
    handleIssueDateChange,
    isContinueDisabled,
    isUploading,
    licenseStateMismatchWarning,
    licenseUploadInputRef,
    today,
    uploadError,
    uploadedLicenseFileName,
    validationError
  } = useLicenseDetailsStep({
    formData,
    updateFormData,
    onContinue
  })

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">Drivers License Details</h1>
        </div>

        <div className="mx-auto max-w-md space-y-4 pt-6">
          {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
          {validationError ? <p className="text-sm text-red-600">{validationError}</p> : null}
          <LicenseIdentityFields
            driverLicenseState={formData.driverLicenseState}
            driverLicenseNumber={formData.driverLicenseNumber}
            isDriverLicenseStateDisabled={isDriverLicenseStateDisabled}
            isDriverLicenseNumberDisabled={isDriverLicenseNumberDisabled}
            licenseStateMismatchWarning={licenseStateMismatchWarning}
            onDriverLicenseStateChange={handleDriverLicenseStateChange}
            onDriverLicenseNumberChange={handleDriverLicenseNumberChange}
          />

          <LicenseDateFields
            issueDate={formData.licenseIssueDate}
            expirationDate={formData.licenseExpirationDate}
            today={today}
            isIssueDateDisabled={isDriverLicenseIssueDateDisabled}
            isExpirationDateDisabled={isDriverLicenseExpiryDisabled}
            onIssueDateChange={handleIssueDateChange}
            onExpirationDateChange={handleExpirationDateChange}
          />

          <LicenseUploadField
            inputRef={licenseUploadInputRef}
            isUploading={isUploading}
            uploadError={uploadError}
            uploadedLicenseFileName={uploadedLicenseFileName}
            onUploadClick={handleDriverLicenseUploadClick}
            onFileChange={handleDriverLicenseFileChange}
          />

          <div className="animate-fade-in-up pt-1" style={{ animationDelay: '460ms' }}>
            <Button
              type="button"
              onClick={handleContinueClick}
              disabled={isSubmitting || isContinueDisabled}
              fullWidth
              className="hover:-translate-y-0.5 active:scale-95"
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </Button>
          </div>

          {/* <div className="animate-fade-in-up" style={{ animationDelay: '520ms' }}>
            <Button
              type="button"
              variant="outline"
              fullWidth
              className="hover:-translate-y-0.5 active:scale-95"
            >
              Save Your Progress
            </Button>
          </div> */}

          <EnrollmentHelpText />
        </div>
      </div>
    </div>
  )
}
